import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Catalog } from 'app/Model/Catalog/Catalog';
import { NewCatalog } from 'app/Model/Dto/NewCatalog';
import { CustomerWiki } from 'app/Model/System/CustomerWiki';
import { Message } from 'app/Model/System/Message';
import { CatalogService } from 'app/Services/CatalogManagement/catalog.service';
import { LoginService } from 'app/Services/login.service';
import { SystemService } from 'app/Services/system.service';
import { UserManagementService } from 'app/Services/userManagment.service';
import { DxDataGridComponent } from 'devextreme-angular';
import { custom } from 'devextreme/ui/dialog';

@Component({
  selector: 'np-catalog-cards',
  templateUrl: './catalog-cards.component.html',
  styleUrl: './catalog-cards.component.css'
})
export class CatalogCardsComponent implements OnInit {
  @ViewChild(DxDataGridComponent, { static: true }) dataGrid: DxDataGridComponent;

  @Input() helpSwitch = false;
  @Input() containerId: string = null;
  @Input() stage: string = null;

  welcomePopupVisible: boolean = false;
  isDataProcessContainer: boolean = false;
  catalogs: Catalog[] = null;
  backUpVisible: boolean = false;
  jobDataVisible: boolean = false;
  addCatalogVisible: boolean = false;
  newCatalogModel: NewCatalog = new NewCatalog();
  noDataText = '';
  datagridLoaded = false;
  catalogToDelete: Catalog;
  loading: boolean = false;

  searchText: string = '';
  searchNames: string[] = [];
  foundCatalogs: Catalog[] = [];

  get model() {
    return !this.isDataProcessContainer ? this.catalogService.catalogs : this.catalogs;
  }

  constructor(
    public loginService: LoginService,
    public systemService: SystemService,
    public translate: TranslateService,
    public userManagment: UserManagementService,
    public catalogService: CatalogService,
    public router: Router
  ) {
    this.onExpandAll = this.onExpandAll.bind(this);
    this.onCollapseAll = this.onCollapseAll.bind(this);
    this.refresh = this.refresh.bind(this);
    this.duplicateCatalog = this.duplicateCatalog.bind(this);
    this.copyCatalog = this.copyCatalog.bind(this);
    this.createRestorePoint = this.createRestorePoint.bind(this);
    this.showCustomDeleteDialog = this.showCustomDeleteDialog.bind(this);

    this.systemService.currentNavigationTitle = this.translate.instant('Katalog Übersicht');
    this.loginService.selectedProductTabIndex = 0;
  }

  ngOnInit() {
    this.isDataProcessContainer = !!this.containerId && !!this.stage;
    this.noDataText = this.translate.instant(
      this.isDataProcessContainer ? 'keine Kataloge im Container' : 'keine Kataloge im Account'
    );
    this.refresh();
  }

  refresh() {
    this.datagridLoaded = false;
    this.catalogService.selectedCatalogId = null;
    this.catalogService.isJobRunning = false;
    this.catalogService.importCatalogVisible = false;
    if (this.loginService.currentUser == null) {
      this.router.navigate(['/']);
      return;
    }
    this.catalogService.catalog = null;

    this.loadCatalogs().then(() => {
      this.foundCatalogs = this.model;
      this.searchNames = this.model.map((catalog) => catalog.catalogName)
    });

    if (this.loginService.currentUser.newUser) {
      this.welcomePopupVisible = true;
      this.loginService.currentUser.newUser = false;
      this.loginService.updateUser(this.loginService.currentUser);
    }
  }

  async loadCatalogs() {
    this.catalogService.setCatalogs(this.loginService.currentUser.customerId);
  }

  isGastro(): boolean {
    return this.loginService.currentCustomer.isGastro;
  }

  updateSearchText() {
    if (this.searchText?.trim() === '') {
      this.foundCatalogs = this.model;
    }
    this.foundCatalogs = this.model.filter((catalog) => catalog.catalogName.toLowerCase().includes(this.searchText.toLowerCase()));
  }

  onRowPrepared(e) {
    if (e.rowType == 'data') {
      if (e.data.isVirtual == true) {
        e.rowElement.style.backgroundColor = '#D6F5F3';
        e.rowElement.className = e.rowElement.className.replace('dx-row-alt', '');
      }

      if (e.data.isDemoCatalog) {
        e.rowElement.style.backgroundColor = 'rgba(255, 122, 89, 0.2)';
        e.rowElement.className = e.rowElement.className.replace('dx-row-alt', '');
      }
    }
  }

  onExpandAll() {
    this.dataGrid.instance.expandAll();
  }

  onCollapseAll() {
    this.dataGrid.instance.collapseAll();
  }

  get importCatalogVisible(): boolean {
    return this.catalogService.importCatalogVisible;
  }

  set importCatalogVisible(value: boolean) {
    this.catalogService.importCatalogVisible = value;
  }

  getLanguageFlag(language: string) {
    if (!language) return '';

    return this.loginService.getLanguageFlag(language.toLowerCase());
  }

  showAddCatalog() {
    this.addCatalogVisible = true;
  }

  showCustomDeleteDialog(e) {
    let catalogToDeleteIsVirtual = e.row.data.isVirtual;
    this.catalogToDelete = e.row.data;
    const virtualCatalogs = this.catalogService.catalogs.filter((catalog) => catalog.isVirtual);
    if (!catalogToDeleteIsVirtual && this.catalogService.catalogs.some((catalog) => catalog.isVirtual)) {
      this.systemService.notify(
        new Message(this.translate.instant('Prüfe Verknüpfungen zu virtuellen Katalogen.')),
        1000
      );
      this.loading = true;
      this.catalogService
        .checkVirtualCatalogs(this.catalogToDelete.id, this.loginService.currentCustomer.id)
        .then((result: Record<string, number>) => {
          this.loading = false;
          if (Object.keys(result).length > 0) {
            this.showDeleteDialogWithVirtualCatalogInfo(result);
          } else {
            this.showDeleteDialog();
          }
        });
    } else {
      this.showDeleteDialog();
    }
  }

  showDeleteDialog() {
    let normalCatalogDialog = custom({
      showTitle: true,
      title: this.translate.instant('Katalog löschen'),
      dragEnabled: false,
      messageHtml: this.translate.instant('Wollen Sie den Katalog wirklich löschen?'),

      buttons: [
        {
          text: this.translate.instant('Ja'),
          onClick: (e) => {
            this.onDeleteClicked();
          }
        },
        {
          text: this.translate.instant('Nein')
        }
      ]
    });
    normalCatalogDialog.show();
  }

  showDeleteDialogWithVirtualCatalogInfo(result: Record<string, number>) {
    var rootUrl = window.location.origin;
    var catalog: Catalog;
    let messageText =
      '<p>' +
      this.translate.instant('DeleteNoteVirtualCatalogs') +
      " <a href='https://knowledge.nextpim.de/wichtige-funktionen#virtuelle-kataloge' target='_blank'>nextPIM Wiki</a>.</p><p>Anzahl Verlinkungen:</p>" +
      '<table><th>Katalog</th><th>Anzahl</th>';
    for (const [key, value] of Object.entries(result)) {
      catalog = this.catalogService.catalogs.find((cat) => cat.id == key);
      if (catalog) {
        messageText += `<tr><td><a href="${rootUrl}/catalog/${this.loginService.currentCustomer.id}/${catalog.id}/info">${catalog.catalogName}</a></td><td style="text-align: center; vertical-align: middle;">${value}</td></tr>`;
      }
    }
    messageText += '</table>';

    let virtualCatalogDialog = custom({
      showTitle: true,
      title: this.translate.instant('Katalog löschen'),
      dragEnabled: false,
      messageHtml: messageText,

      buttons: [
        {
          text: 'Löschen bestätigen',
          onClick: (e) => {
            this.onDeleteClicked();
          }
        },
        {
          text: 'Abbrechen',
          onClick: (e) => {}
        }
      ]
    });
    virtualCatalogDialog.show();
  }

  showImportCatalog() {
    this.catalogService.importCatalogVisible = true;
  }

  onEditClicked(e) {
    this.catalogService.selectedCatalogId = e.key.id;
    this.router.navigate(['/catalog/' + e.key.customerId + '/' + e.key.id + '/info']);
  }

  catalogSelected(data: Catalog) {
    this.catalogService.selectedCatalogId = data.id;
    this.router.navigate(['/catalog/' + data.customerId + '/' + data.id + '/info']);
  }

  updateResponsible(data) {
    this.catalogService.setResponsible();
    this.catalogService.catalog = data.data;
    this.catalogService.updateCatalog();
  }

  update(data) {
    this.catalogService.catalog = data.data;
    this.catalogService.updateCatalog();
  }

  duplicateCatalog(e) {
    this.translate.get('Der Katalog wird dupliziert').subscribe((text: string) => {
      this.systemService.notify(new Message(text));
    });
    this.catalogService.duplicateCatalog(e.row.data.id);
  }

  createRestorePoint(e) {
    this.translate.get('Ein Wiederherstellungspunkt wird per Job erzeugt').subscribe((text: string) => {
      this.systemService.notify(new Message(text));
    });
    this.catalogService.createRestorePoint(e.row.data.id);
  }

  onDeleteClicked() {
    if (
      this.loginService.hasAddonONBOARDING &&
      this.catalogService.catalogs.length > 0 &&
      this.catalogToDelete.isDemoCatalog &&
      this.catalogService.catalogs.filter((cat) => cat.isDemoCatalog).length == 1
    ) {
      this.systemService.notifyInfo(
        this.translate.instant(
          'Demo-Katalog kann nicht gelöscht werden. Es muss mindestens ein Demo-Katalog vorhanden sein.'
        )
      );
    } else {
      this.catalogService.remCatalog(this.catalogToDelete.id);
    }
    this.refresh();
  }

  copyCatalog(e) {
    this.catalogService.copyCatalogId = e.row.data.id;
    this.catalogService.copyCatalogCustomerId = this.loginService.currentUser.customerId;
  }

  get copyCatalogId() {
    return this.catalogService.copyCatalogId;
  }

  pasteCatalog() {
    this.translate.get('Katalog wird eingefügt').subscribe((text: string) => {
      this.systemService.notify(new Message(text));
    });

    this.catalogService.duplicateCatalog(this.catalogService.copyCatalogId, this.catalogService.copyCatalogCustomerId);
  }

  get fullScreen() {
    return this.catalogService.viewService.fullScreen;
  }

  set fullScreen(value: boolean) {
    this.catalogService.viewService.fullScreen = value;
  }

  toFullScreen() {
    this.catalogService.viewService.fullScreen = true;
  }

  toWindow() {
    this.catalogService.viewService.fullScreen = false;
  }

  hideExport() {
    this.catalogService.exportVisible = false;
  }

  showBackUp() {
    this.backUpVisible = true;
  }

  hideBackUp() {
    this.backUpVisible = false;
  }

  showJobData() {
    this.jobDataVisible = true;
  }

  hideJobData() {
    this.jobDataVisible = false;
  }

  toggleHelp() {
    this.systemService.showHelp = !this.systemService.showHelp;
    if (this.systemService.showHelp) {
      this.systemService
        .loadHelp(this.loginService.currentUser.customerId)
        .subscribe((result: CustomerWiki) => (this.systemService.help = result.entries));
    }
  }

  //SETTINGS
  get showToolTips(): boolean {
    return this.loginService.showToolTips;
  }

  get showProToolTips(): boolean {
    return this.loginService.showProToolTips;
  }

  get showPlaceHolders(): boolean {
    return this.loginService.showPlaceHolders;
  }

  mimedate() {
    return '?' + new Date().toDateString;
  }

  imageVisable: boolean = false;
  source: string = '';
  target: string = '';

  showImage(source, index) {
    if (source != null) {
      this.target = '#link' + index;
      this.source = source;
      this.imageVisable = true;
    }
  }

  hideImage() {
    this.imageVisable = false;
  }

  memoVisable: boolean = false;
  memoText: string = '';
  memoTarget: string = '';

  showMemo(memo, index) {
    if (memo.data.memo != null) {
      this.memoTarget = '#memo' + memo.rowIndex;
      this.memoText = memo.data.memo;
      this.memoVisable = true;
    }
  }

  hideMemo() {
    this.memoVisable = false;
  }

  canView(name: string): boolean {
    return this.userManagment.canView(name);
  }
  canEdit(name: string): boolean {
    return this.userManagment.canEdit(name);
  }
}
